import { useRef, useState } from 'react';
import {
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  Repeat2,
  ThumbsUp,
  Share2,
  Globe,
  Image as ImageIcon,
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  MediaAttachment,
  PlatformPostSettings,
  PublishAttemptResult,
  SocialMediaAccountPublic,
  SocialPlatform,
} from '@/lib/api';

interface PostPreviewProps {
  content: string;
  accounts: SocialMediaAccountPublic[];
  targetAccountIds: string[];
  mediaAttachments: MediaAttachment[];
  /** Per-platform text overrides from AI generation; falls back to `content` when absent */
  platformTextOverrides?: Record<string, string>;
  /** Per-platform publish results; shows error or link below each card */
  publishResults?: Partial<Record<SocialPlatform, PublishAttemptResult>>;
  /** Link URL to embed in platforms that have includeLink enabled */
  link?: string;
  /** Per-platform settings (includeLink, format, etc.) */
  perPlatformSettings?: Record<string, Partial<PlatformPostSettings>>;
  /** Platforms the user has approved for publish; when non-empty, only show approved ones */
  approvedPlatforms?: Record<string, boolean>;
}

const PLATFORM_ORDER: SocialPlatform[] = ['instagram', 'twitter', 'linkedin', 'facebook'];

function getPreviewAccount(
  accounts: SocialMediaAccountPublic[],
  targetAccountIds: string[],
): SocialMediaAccountPublic | null {
  if (targetAccountIds.length > 0) {
    const found = accounts.find(
      (a) => targetAccountIds.includes(a.accountId) && a.status === 'active',
    );
    if (found) return found;
  }
  return accounts.find((a) => a.status === 'active') ?? null;
}

function getAccountForPlatform(
  accounts: SocialMediaAccountPublic[],
  targetAccountIds: string[],
  platform: SocialPlatform,
): SocialMediaAccountPublic | null {
  // Prefer a targeted account for this platform, then any active account for it
  if (targetAccountIds.length > 0) {
    const targeted = accounts.find(
      (a) =>
        a.platform === platform && targetAccountIds.includes(a.accountId) && a.status === 'active',
    );
    if (targeted) return targeted;
  }
  return accounts.find((a) => a.platform === platform && a.status === 'active') ?? null;
}

function AvatarCircle({
  account,
  sizeClass = 'h-10 w-10',
}: {
  account: SocialMediaAccountPublic | null;
  sizeClass?: string;
}) {
  if (account?.platformAvatarUrl) {
    return (
      <img
        src={account.platformAvatarUrl}
        alt=""
        className={cn('rounded-full flex-shrink-0 object-cover', sizeClass)}
      />
    );
  }
  const letter = account ? (account.platformName ?? account.platform)[0].toUpperCase() : '?';
  return (
    <div
      className={cn(
        'rounded-full bg-muted flex items-center justify-center text-muted-foreground font-bold flex-shrink-0',
        sizeClass,
      )}
    >
      {letter}
    </div>
  );
}

/**
 * Inline video player with autoplay, muted loop, and hover controls for
 * play/pause and mute toggle.
 */
function VideoPlayer({ src, className }: { src: string; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      void videoRef.current.play();
      setIsPaused(false);
    } else {
      videoRef.current.pause();
      setIsPaused(true);
    }
  };

  return (
    <div className={cn('relative group', className)}>
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        className="w-full h-full object-cover"
      />
      {/* Controls shown on hover */}
      <div className="absolute bottom-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPaused ? 'Play' : 'Pause'}
          className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
        >
          {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
          className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
        >
          {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}

/** Renders a single media item — video player or image based on mimeType. */
function MediaItem({ attachment, className }: { attachment: MediaAttachment; className?: string }) {
  if (attachment.mimeType.startsWith('video/')) {
    return <VideoPlayer src={attachment.previewUrl} className={cn('w-full h-full', className)} />;
  }
  return (
    <img
      src={attachment.previewUrl}
      alt=""
      className={cn('w-full h-full object-cover', className)}
    />
  );
}

function MediaPlaceholder({ attachment }: { attachment?: MediaAttachment }) {
  if (attachment) {
    return <MediaItem attachment={attachment} className="w-full h-full" />;
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
      <ImageIcon className="h-10 w-10" />
      <p className="text-xs">Image preview</p>
    </div>
  );
}

/**
 * Twitter supports up to 4 media items with specific grid layouts:
 *  1 → full-width 16:9
 *  2 → side-by-side columns
 *  3 → left tall + right column with 2 stacked
 *  4 → 2×2 quadrant
 */
function TwitterMediaGrid({ media }: { media: MediaAttachment[] }) {
  const capped = media.slice(0, 4);
  const count = capped.length;

  if (count === 0) return null;

  if (count === 1) {
    return (
      <div className="mt-3 rounded-xl overflow-hidden aspect-video bg-muted">
        <MediaItem attachment={capped[0]} />
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className="mt-3 grid grid-cols-2 gap-0.5 rounded-xl overflow-hidden h-48">
        {capped.map((a, i) => (
          <MediaItem key={i} attachment={a} />
        ))}
      </div>
    );
  }

  if (count === 3) {
    return (
      <div className="mt-3 grid grid-cols-2 grid-rows-2 gap-0.5 rounded-xl overflow-hidden h-48">
        {/* Left item spans both rows */}
        <MediaItem attachment={capped[0]} className="row-span-2 w-full h-full" />
        <MediaItem attachment={capped[1]} />
        <MediaItem attachment={capped[2]} />
      </div>
    );
  }

  // 4 items — quadrant
  return (
    <div className="mt-3 grid grid-cols-2 gap-0.5 rounded-xl overflow-hidden">
      {capped.map((a, i) => (
        <div key={i} className="aspect-square">
          <MediaItem attachment={a} />
        </div>
      ))}
    </div>
  );
}

/**
 * Shared carousel UI for Instagram and Facebook.
 * Handles slide navigation and dot indicators.
 */
function CarouselSlider({ media }: { media: MediaAttachment[] }) {
  const [slide, setSlide] = useState(0);
  const capped = media.slice(0, 10);

  if (capped.length === 0) {
    return (
      <div className="w-full aspect-square bg-muted overflow-hidden">
        <MediaPlaceholder />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-square bg-muted overflow-hidden">
      <MediaItem attachment={capped[slide]} />

      {capped.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous"
            onClick={() => setSlide((s) => Math.max(0, s - 1))}
            disabled={slide === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/50 text-white flex items-center justify-center disabled:opacity-30 transition-opacity"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => setSlide((s) => Math.min(capped.length - 1, s + 1))}
            disabled={slide === capped.length - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/50 text-white flex items-center justify-center disabled:opacity-30 transition-opacity"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1.5">
            {capped.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Slide ${i + 1}`}
                onClick={() => setSlide(i)}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-colors',
                  i === slide ? 'bg-white' : 'bg-white/50',
                )}
              />
            ))}
          </div>

          {/* Slide counter */}
          <div className="absolute top-2 right-2 rounded-full bg-black/50 text-white text-xs px-1.5 py-0.5">
            {slide + 1}/{capped.length}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A mocked link-preview card matching the style platforms typically render
 * when a URL is included in a post.
 */
function LinkPreviewCard({ url }: { url: string }) {
  let domain = url;
  try {
    domain = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    // keep raw url if invalid
  }

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/50 overflow-hidden flex items-center gap-2 px-3 py-2">
      <Link2 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{domain}</p>
        <p className="text-[10px] text-muted-foreground truncate">{url}</p>
      </div>
    </div>
  );
}

function InstagramPreview({
  account,
  content,
  media,
  format,
}: {
  account: SocialMediaAccountPublic | null;
  content: string;
  media: MediaAttachment[];
  format?: 'post' | 'reel' | 'carousel' | 'story';
}) {
  // Derive initial preview mode from the format setting; fall back to 'reel'
  const initialMode: 'reel' | 'carousel' = format === 'carousel' ? 'carousel' : 'reel';
  const [mode, setMode] = useState<'reel' | 'carousel'>(initialMode);
  const username = account?.platformUsername ?? account?.platformName ?? 'yourhandle';
  const hasMultiple = media.length > 1;

  // Story format: full-screen 9:16 vertical with text overlay
  if (format === 'story') {
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden w-full shadow-sm">
        <div className="relative w-full" style={{ aspectRatio: '9/16' }}>
          <div className="absolute inset-0 bg-muted">
            {media[0] && <MediaItem attachment={media[0]} className="w-full h-full" />}
          </div>
          {/* Story header overlay */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <AvatarCircle account={account} sizeClass="h-8 w-8" />
            <span className="text-xs font-semibold text-white drop-shadow">{username}</span>
          </div>
          {/* Caption overlay at bottom */}
          {content.trim() && (
            <div className="absolute bottom-10 inset-x-3">
              <p className="text-xs text-white drop-shadow line-clamp-3">{content}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full shadow-sm">
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <AvatarCircle account={account} sizeClass="h-8 w-8" />
          <span className="text-sm font-semibold text-foreground">{username}</span>
        </div>

        {/* Reel / Carousel toggle — only shown when media present and no explicit format */}
        {media.length > 0 && !format && (
          <div className="flex items-center gap-0.5 rounded-full bg-muted p-0.5">
            <button
              type="button"
              onClick={() => setMode('reel')}
              className={cn(
                'px-2 py-0.5 rounded-full text-xs transition-colors',
                mode === 'reel'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Reel
            </button>
            <button
              type="button"
              onClick={() => setMode('carousel')}
              className={cn(
                'px-2 py-0.5 rounded-full text-xs transition-colors',
                mode === 'carousel'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {hasMultiple ? `Carousel (${Math.min(media.length, 10)})` : 'Carousel'}
            </button>
          </div>
        )}
        {/* Format badge when explicitly set */}
        {format && (
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground bg-muted rounded-full px-2 py-0.5">
            {format}
          </span>
        )}

        <span className="text-muted-foreground font-bold tracking-widest">···</span>
      </div>

      {format === 'carousel' || mode === 'carousel' ? (
        <CarouselSlider media={media} />
      ) : (
        <div className="w-full aspect-square bg-muted overflow-hidden">
          <MediaPlaceholder attachment={media[0]} />
        </div>
      )}

      <div className="px-3 py-2.5 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Heart className="h-6 w-6 text-foreground" />
            <MessageCircle className="h-6 w-6 text-foreground" />
            <Send className="h-6 w-6 text-foreground" />
          </div>
          <Bookmark className="h-6 w-6 text-foreground" />
        </div>
        <div>
          {content.trim() ? (
            <p className="text-sm text-foreground">
              <span className="font-semibold mr-1">{username}</span>
              {content}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground italic">Your caption will appear here…</p>
          )}
        </div>
      </div>
    </div>
  );
}

function TwitterPreview({
  account,
  content,
  media,
  linkUrl,
}: {
  account: SocialMediaAccountPublic | null;
  content: string;
  media: MediaAttachment[];
  linkUrl?: string;
}) {
  const displayName = account?.platformName ?? 'Your Name';
  const handle = account?.platformUsername ?? 'yourhandle';

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full shadow-sm p-4 space-y-3">
      <div className="flex items-start gap-3">
        <AvatarCircle account={account} sizeClass="h-10 w-10" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-foreground">{displayName}</span>
            <span className="text-sm text-muted-foreground">@{handle}</span>
          </div>
          <div className="mt-1.5">
            {content.trim() ? (
              <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">Your tweet will appear here…</p>
            )}
          </div>
          <TwitterMediaGrid media={media} />
          {linkUrl && <LinkPreviewCard url={linkUrl} />}
          <div className="flex items-center gap-6 mt-3 text-muted-foreground">
            <MessageCircle className="h-4 w-4" />
            <Repeat2 className="h-4 w-4" />
            <Heart className="h-4 w-4" />
            <BarChart2 className="h-4 w-4" />
            <Share2 className="h-4 w-4" />
          </div>
        </div>
      </div>
    </div>
  );
}

function LinkedInPreview({
  account,
  content,
  media,
  linkUrl,
}: {
  account: SocialMediaAccountPublic | null;
  content: string;
  media: MediaAttachment[];
  linkUrl?: string;
}) {
  const displayName = account?.platformName ?? 'Your Name';
  const handle = account?.platformUsername;
  const first = media[0];

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full shadow-sm">
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <AvatarCircle account={account} sizeClass="h-12 w-12" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{displayName}</p>
            {handle && <p className="text-xs text-muted-foreground">@{handle} · 1st</p>}
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <span>Just now</span>
              <span>·</span>
              <Globe className="h-3 w-3" />
            </div>
          </div>
        </div>
        {content.trim() ? (
          <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">Your post will appear here…</p>
        )}
        {linkUrl && <LinkPreviewCard url={linkUrl} />}
      </div>
      {first ? (
        <div className="aspect-video bg-muted overflow-hidden">
          <MediaItem attachment={first} />
        </div>
      ) : null}
      {media.length > 1 && (
        <p className="px-4 py-1.5 text-xs text-muted-foreground border-t border-border">
          +{media.length - 1} more {media.length > 2 ? 'files' : 'file'}
        </p>
      )}
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center gap-5 text-muted-foreground">
          <div className="flex items-center gap-1.5 text-xs">
            <ThumbsUp className="h-4 w-4" />
            <span>Like</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <MessageCircle className="h-4 w-4" />
            <span>Comment</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Repeat2 className="h-4 w-4" />
            <span>Repost</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Send className="h-4 w-4" />
            <span>Send</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FacebookPreview({
  account,
  content,
  media,
  linkUrl,
}: {
  account: SocialMediaAccountPublic | null;
  content: string;
  media: MediaAttachment[];
  linkUrl?: string;
}) {
  const displayName = account?.platformName ?? 'Your Name';

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full shadow-sm">
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <AvatarCircle account={account} sizeClass="h-10 w-10" />
          <div>
            <p className="text-sm font-semibold text-foreground">{displayName}</p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <span>Just now</span>
              <span>·</span>
              <Globe className="h-3 w-3" />
            </div>
          </div>
        </div>
        {content.trim() ? (
          <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">Your post will appear here…</p>
        )}
        {linkUrl && <LinkPreviewCard url={linkUrl} />}
      </div>

      {/* Facebook uses carousel for multiple media */}
      {media.length > 0 && <CarouselSlider media={media} />}

      <div className="px-4 py-2 border-t border-border">
        <div className="flex items-center justify-around text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs py-1.5 hover:text-foreground transition-colors"
          >
            <ThumbsUp className="h-4 w-4" />
            <span>Like</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs py-1.5 hover:text-foreground transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            <span>Comment</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs py-1.5 hover:text-foreground transition-colors"
          >
            <Share2 className="h-4 w-4" />
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function renderPlatformCard(
  platform: SocialPlatform,
  account: SocialMediaAccountPublic | null,
  content: string,
  media: MediaAttachment[],
  settings?: Partial<PlatformPostSettings>,
  link?: string,
) {
  // Resolve link: only show it when the platform's includeLink setting is true
  const resolvedLink =
    link && settings && 'includeLink' in settings && settings.includeLink ? link : undefined;

  switch (platform) {
    case 'twitter':
      return (
        <TwitterPreview account={account} content={content} media={media} linkUrl={resolvedLink} />
      );
    case 'linkedin':
      return (
        <LinkedInPreview account={account} content={content} media={media} linkUrl={resolvedLink} />
      );
    case 'facebook':
      return (
        <FacebookPreview account={account} content={content} media={media} linkUrl={resolvedLink} />
      );
    default: {
      const igSettings = settings as
        | { format?: 'post' | 'reel' | 'carousel' | 'story' }
        | undefined;
      return (
        <InstagramPreview
          account={account}
          content={content}
          media={media}
          format={igSettings?.format}
        />
      );
    }
  }
}

function PublishResultBadge({ result }: { result: PublishAttemptResult | undefined }) {
  if (!result) return null;

  if (result.success && result.url) {
    return (
      <a
        href={result.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
      >
        View published post ↗
      </a>
    );
  }

  if (result.success) {
    return <p className="text-xs font-medium text-emerald-600">Published successfully</p>;
  }

  return (
    <p className="text-xs text-destructive">
      {result.error ?? 'Publish failed'}
      {result.errorCode ? ` (${result.errorCode})` : ''}
    </p>
  );
}

export function PostPreview({
  content,
  accounts,
  targetAccountIds,
  mediaAttachments,
  platformTextOverrides,
  publishResults,
  link,
  perPlatformSettings,
  approvedPlatforms,
}: PostPreviewProps) {
  const hasPlatformContent = platformTextOverrides && Object.keys(platformTextOverrides).length > 0;
  const hasApprovals = approvedPlatforms && Object.values(approvedPlatforms).some(Boolean);

  // When AI captions are generated, show a preview card for every platform that has content.
  // When approvals exist, only show approved platforms.
  if (hasPlatformContent) {
    const platformsWithContent = PLATFORM_ORDER.filter((p) => p in platformTextOverrides!);
    const platformsToShow = hasApprovals
      ? platformsWithContent.filter((p) => approvedPlatforms![p])
      : platformsWithContent;

    return (
      <div className="flex flex-col items-center gap-6 py-6 px-4">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-semibold text-foreground">Preview</h2>
          <p className="text-xs text-muted-foreground">Final appearance may vary by platform</p>
        </div>

        {platformsToShow.length === 0 && (
          <p className="text-sm text-muted-foreground">Approve a platform to see its preview.</p>
        )}

        {platformsToShow.map((platform) => {
          const account = getAccountForPlatform(accounts, targetAccountIds, platform);
          const displayContent = platformTextOverrides![platform];
          const result = publishResults?.[platform];
          const settings = perPlatformSettings?.[platform];
          const card = renderPlatformCard(
            platform,
            account,
            displayContent,
            mediaAttachments,
            settings,
            link,
          );
          return (
            <div key={platform} className="w-full max-w-sm space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground capitalize">
                {platform === 'twitter' ? 'X / Twitter' : platform}
              </p>
              {result?.success && result.url ? (
                <a href={result.url} target="_blank" rel="noopener noreferrer" className="block">
                  {card}
                </a>
              ) : (
                card
              )}
              <PublishResultBadge result={result} />
            </div>
          );
        })}
      </div>
    );
  }

  // Default: single card based on the selected (or first active) account
  const account = getPreviewAccount(accounts, targetAccountIds);
  const platform: SocialPlatform = (account?.platform ?? 'instagram') as SocialPlatform;
  const result = publishResults?.[platform];
  const settings = perPlatformSettings?.[platform];
  const card = renderPlatformCard(platform, account, content, mediaAttachments, settings, link);

  return (
    <div className="flex flex-col items-center gap-5 py-6 px-4">
      <div className="text-center space-y-1">
        <h2 className="text-lg font-semibold text-foreground">Preview</h2>
        <p className="text-xs text-muted-foreground">Final appearance may vary by platform</p>
      </div>
      <div className="w-full max-w-sm space-y-2">
        {result?.success && result.url ? (
          <a href={result.url} target="_blank" rel="noopener noreferrer" className="block">
            {card}
          </a>
        ) : (
          card
        )}
        <PublishResultBadge result={result} />
      </div>
    </div>
  );
}
